import { DmpConnectorSettings, DmpVisibility, SecurityLabelEnum } from '@domain/types';
import { computeDmpVisibility } from '@domain/utils';
import { Form, Input, Radio, RadioChangeEvent, Space, Typography } from '@honestica/ui-kit/src';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type DmpSettingsProps = {
  originalSettings?: DmpConnectorSettings | undefined;
  settings: DmpConnectorSettings | undefined;
  setSettings: Dispatch<SetStateAction<DmpConnectorSettings>>;
  isPatientMinor: boolean;
  isReadOnly: boolean;
};

const VISIBILITY_TO_SECURITY_LABEL_MAPPING: Record<DmpVisibility, SecurityLabelEnum | undefined> = {
  [DmpVisibility.HideForParents]: undefined,
  [DmpVisibility.HideForPatient]: SecurityLabelEnum.HideForPatient,
  [DmpVisibility.HideForProfessionals]: SecurityLabelEnum.HideForProfessionals,
  [DmpVisibility.VisibleForAll]: undefined,
};

export function DmpSettingsFormItems({
  settings,
  setSettings,
  isPatientMinor,
  isReadOnly,
}: DmpSettingsProps) {
  const { t } = useTranslation();

  const availableVisibilityOptions = useMemo(() => {
    const allOptions = Object.values(DmpVisibility);
    if (!isPatientMinor) {
      return allOptions.filter((val) => val !== DmpVisibility.HideForParents);
    }
    return allOptions;
  }, [isPatientMinor]);

  if (!settings) return null;

  const visibility: DmpVisibility = computeDmpVisibility(settings);

  const updateVisibility = (e: RadioChangeEvent) => {
    const securityLabels: SecurityLabelEnum[] = [];

    const value: DmpVisibility = e.target.value;

    const label = VISIBILITY_TO_SECURITY_LABEL_MAPPING[value];
    if (label !== undefined) {
      securityLabels.push(label);
    }

    setSettings((s) => ({ ...s, securityLabels: securityLabels }));
  };

  return (
    <div className="padding-lg">
      <Form.Item
        htmlFor="documentTitle"
        label={
          <Typography.Text strong>
            {t('documents.integrations.dmp.settings.documentTitle')}
          </Typography.Text>
        }
      >
        <Input
          aria-label="documentTitle"
          id="documentTitle"
          data-testid="modal-draft-dmp-settings-document-title-input"
          value={settings.documentTitle}
          onChange={(e) => setSettings((s) => ({ ...s, documentTitle: e.target.value }))}
          disabled={isReadOnly}
        />
      </Form.Item>
      <Form.Item
        htmlFor="visibility"
        label={
          <Typography.Text strong>
            {t('documents.integrations.dmp.settings.visibility')}
          </Typography.Text>
        }
      >
        <Radio.Group
          id="visibility"
          value={visibility}
          onChange={updateVisibility}
          disabled={isReadOnly}
        >
          <Space direction="vertical">
            {availableVisibilityOptions.map((option) => (
              <Radio value={option} key={option} aria-label={option}>
                {t(`documents.integrations.dmp.visibility.${option}`)}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
    </div>
  );
}
